import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import Swal from "sweetalert2";
import Empty from "../../assets/images/appImages/category.png";
import { ChooseSizeModal } from "../HomePage/ChooseSizeModal";
import { useAppDispatch } from "../../redux/hook";
import {
  addCartProduct,
  getCartCount,
  getTotalAmount,
  type CartItemType,
} from "../../redux/slice/useCartSlice";
import { getImage, truncateCaractere } from "../../utils/Utils";
import {
  getDefaultCartVariation,
  getProductPriceLabels,
  getProductPromoRate,
  getUnitPriceForVariation,
  needsSizePickerOnCard,
  isProductAvailable,
  getCartStockLimit,
} from "../../utils/productPricing";
import { Color } from "../../utils/theme";

interface ProduitCardProps {
  item: any;
}

function getVendeurId(item: any): number {
  if (typeof item?.vendeur === "object" && item?.vendeur !== null && "id" in item.vendeur) {
    return Number((item.vendeur as { id?: string | number }).id) || 0;
  }
  return Number(item?.vendeur) || 0;
}

const ProduitCard: React.FC<ProduitCardProps> = ({ item }) => {
  const dispatch = useAppDispatch();
  const [showSizePicker, setShowSizePicker] = useState(false);

  const imgProd =
    item?.type === "taille_unique"
      ? item?.images?.[0]?.image
      : item?.variations?.[0]?.images?.[0]?.image;

  const devise = item?.devise || "XOF";
  const priceLabels = getProductPriceLabels(item, devise);
  const quantityLabel = item?.contenance ?? item?.poids ?? item?.volume ?? "";
  const badgeLabel =
    item?.badge ?? (item?.new ? "Essentiel" : item?.best_seller ? "Best-seller" : "");

  const promoRate = getProductPromoRate(item);
  const showSizePickerUI = needsSizePickerOnCard(item);
  const defaultVariation = getDefaultCartVariation(item);
  const cartVariations: any[] = defaultVariation ? [defaultVariation] : [];
  const prixFinal = getUnitPriceForVariation(item, defaultVariation, promoRate);
  const available = isProductAvailable(item);

  const addToCartDirect = () => {
    if (!defaultVariation?.id && item?.variations?.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Variation indisponible",
        text: "Ce produit n'est pas disponible pour le moment.",
        iconColor: Color.success,
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    const produit: CartItemType = {
      produit: item,
      prix: prixFinal,
      quantite: 1,
      stock: getCartStockLimit(item, defaultVariation),
      variations: cartVariations,
      vendeur: getVendeurId(item),
    };

    dispatch(addCartProduct(produit));
    dispatch(getCartCount());
    dispatch(getTotalAmount());
    Swal.fire({
      icon: "success",
      iconColor: Color.success,
      confirmButtonColor: Color.success,
      title: "Produit ajouté au panier avec succès !",
      showConfirmButton: false,
      timer: 2000,
    });
  };

  const handleAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!available) {
      Swal.fire({
        icon: "info",
        title: "Produit indisponible",
        text: "Ce produit est en rupture de stock.",
        iconColor: Color.success,
        showConfirmButton: false,
        timer: 2500,
      });
      return;
    }

    if (showSizePickerUI) {
      setShowSizePicker(true);
      return;
    }

    addToCartDirect();
  };

  return (
    <>
      <NavLink
        to={`/produit/${item?.slug}`}
        state={item}
        className="fb-produit-card no-link"
      >
        <div className="fb-produit-card-image">
          <img
            loading="lazy"
            src={imgProd ? getImage(imgProd) : Empty}
            alt={item?.nom}
          />
          {badgeLabel && <span className="fb-produit-badge">{badgeLabel}</span>}
        </div>

        <div className="fb-produit-card-body">
          {item?.categorie?.nom && (
            <p className="fb-produit-card-category">{item.categorie.nom}</p>
          )}
          <h3 className="fb-produit-card-title">
            {truncateCaractere(item?.nom, 40)}
          </h3>

          <div className="fb-produit-card-footer">
            <div className="fb-produit-card-price-block">
              <span className="fb-produit-card-price">{priceLabels.display}</span>
              {priceLabels.displayMru && (
                <span className="fb-produit-card-price-mru">
                  {priceLabels.displayMru}
                </span>
              )}
              {quantityLabel && (
                <span className="fb-produit-card-qty">{quantityLabel}</span>
              )}
            </div>
            <button
              type="button"
              className="btn fb-btn-primary fb-produit-card-add"
              onClick={handleAdd}
              disabled={!available}
            >
              {!available
                ? "Indisponible"
                : showSizePickerUI
                  ? "Choisir"
                  : "Ajouter"}
            </button>
          </div>
        </div>
      </NavLink>

      {showSizePickerUI && (
        <ChooseSizeModal
          product={item}
          devise={devise}
          imageUrl={imgProd}
          isOpen={showSizePicker}
          onClose={() => setShowSizePicker(false)}
        />
      )}
    </>
  );
};

export default ProduitCard;
